import React, { useState } from 'react';
import PropTypes from 'prop-types';

import {
  Box,
  Button,
  Checkbox,
  Grid,
  TextField,
  Typography,
  makeStyles,
  useMediaQuery,
  useTheme
} from '@material-ui/core';
import ReactSelect from 'react-select';
import AddExpenseDialogTable from './AddExpenseDialogTable';
import expenseService from '../../../services/ExpenseService';
import toast from 'react-hot-toast';
import { formatReceiptText } from './helper';
import linkEmployeeService from '../../../services/LinkEmployeeService';

const useStyles = makeStyles(() => ({
  typography: {
    marginBottom: '5px'
  }
}));

const AddExpenseDialogContent = ({
  expenseFormData,
  customerRefs,
  classRefs,
  setExpenseFormData,
  itemRefs,
  vendorRefs,
  creditCardAccount,
  rows,
  setRows
}) => {
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
  const classes = useStyles();

  const [receiptFile, setReceiptFile] = useState();
  const [fileExtractedRecord, setFileExtractedRecord] = useState();
  const [loading, setLoading] = useState(false);

  const customerRefOptions = customerRefs.map((c) => {
    return { value: c.name, label: c.name, id: c.value };
  });

  const vendorRefOptions = vendorRefs.map((v) => {
    return { value: v.name, label: v.name, id: v.value };
  });

  const creditCardAccountOptions = creditCardAccount.map((v) => {
    return { value: v.name, label: v.name, id: v.value };
  });

  const globalTaxCalculationOptions = [
    { value: 'TaxExcluded', label: 'TaxExcluded' },
    { value: 'TaxInclusive', label: 'TaxInclusive' }
  ];

  const handleSelectedClient = (selected) => {
    setExpenseFormData({ ...expenseFormData, client: selected });
  };

  const handleSelectedVendor = (selected) => {
    setExpenseFormData({ ...expenseFormData, vendor: selected });
  };

  const handleSelectedCreditCard = (selected) => {
    setExpenseFormData({ ...expenseFormData, creditCardAccount: selected });
  };

  const handleExpenseFormData = (e) => {
    const { name, value, checked } = e.target;

    if (name === 'paidByEmployee' && checked) {
      setExpenseFormData({
        ...expenseFormData,
        paidByEmployee: true,
        paidByCompany: false,
        creditCardAccount: ''
      });
    } else if (name === 'paidByCompany' && checked) {
      setExpenseFormData({
        ...expenseFormData,
        paidByEmployee: false,
        paidByCompany: true
      });
    } else if (name === 'isBillable' && checked) {
      setExpenseFormData({
        ...expenseFormData,
        isBillable: true
      });
    } else {
      setExpenseFormData({
        ...expenseFormData,
        [name]: value
      });
    }
  };

  const handleExpenseReceiptFileChange = (e) => {
    setReceiptFile(e.target.files[0]);

    //update in the formData
    setExpenseFormData({
      ...expenseFormData,
      expenseReceiptFile: e.target.files[0]
    });
  };

  const handleSelectedGlobalTaxCalculation = (selected) => {
    setExpenseFormData({ ...expenseFormData, globalTaxCalculation: { label: selected.value, value: selected.value } });
  };

  const handleReceiptUpload = async () => {
    setLoading(true);

    try {
      // eslint-disable-next-line no-undef
      const formData = new FormData();
      formData.append('file', receiptFile);

      const response = await expenseService.extractReceiptImageText(formData);
      const extractReceiptText = response?.data?.data;
      // console.log('extractReceiptText', extractReceiptText);

      //TODO: this is only demo data, in production use real file text
      // const extractReceiptText =
      //   'Y\nG\nH\n00\nB\nN\nM\nK\nTAX INVOICE / RECEIPT\nThe Cheeky Pint\nFootscray VIC 3011, Australia\nFootscray Victoria 3011\n54484727806\nAlt\n目\nwww.thecheekypint.com.au\nSale #: SP-1 0207011013\n2025-02-07 13:03:11\nServed by Lando B\nBox 7/2\nDescription\nAmount\nSchnity sanga\n$18.00\n5 Eyes IPA - Schooner x 3\n$39.00\nSchnity sanga x 5\n$90.00\nWOTW XPA - Schooner\n$12.00\nVodka\n$13.00\nCoke x 2\n$10.00\nExpress cheeseburger\n$15.00\nSubtotal:\n$197.00\nTotal ex tax:\n$179.09\n- GST\n$17.91\nPayments Surcharge:\n$5.41\nTotal Inc Tax:\n$202.41\nMethod\nAmount\nLightspeed Payments\n$202.41\nPrices shown in AUD\nCustomer Copy\nCard: amex\n2025-02-07 13:03:12\nRegister: Main Cashdraw\nPrinter: Bar\nSale ID: ujtsh1h7\nPayments Surcharge: $5.41\nTotal: $202.41\nAPPROVED\nReference: a315098121\n';

      // const extractReceiptText =
      //   "Coles Supermarkets Australia Pty Ltd\nTax Invoice ABN: 45 004 189 708\ncoles\nStore: 7774 - CS YARRAVILLE\nStore Manager: Chris\nPhone:\n9362-4300\nServed By: Assisted Checkout\nRegister: 122\nDate:\nDescription\n20/12/2024\nReceipt: 9137\nTime: 08:23\n* VIVA PAPER TOWEL WHI 6PACK\n**MULTIX ALFOIL TRAD 60METRE\n2 @ $7.00 EACH\n1 'MPERFECT LEMONS 1KG\n2 $8.90 EACH\nTotal for 5 Items:\nEFT\nGST INCLUDED IN TOTAL\n9.00\n14.00\n17.80\n$40.80\n$40.80\n$2.09\nColes\n20/12/24 08:22\n***** 1047\nAPSN 0000 ATC 0005\nPURCHASE\nRRN 001220913700\nAUTH 831326\nVIC AU\n37912656\nNV74C2\nAMERICAN EXPRESS\nAMERICAN EXPRESS\nA000000025010801\nAUD$ 40.80\n(00) APPROVED\nNO PIN OR SIGNATURE REQUIRED\n= Specials\nDans\n% = Taxable\nitems\n$14.00\nTotal Savings\nTotal Savings include any Promotional\nand Loyalty Discounts.\nSign up now at flybuys.com.au\n12012247774122913700\nEsc\nTab K\nCaps Lock\nBME G\n5 Bonus Kitchen Knife credits\nwhen you join Flybuys!\nJoin by 10/12/24 by downloading the Fiy buys app ar hending to thybuys.com.cu.\nT&Cs apply. See coles.com.au/arneg terins\nScan your Flybuys and receive one Kitchen Knife credit\nand competition entry for every $20 you spend\nYou could have earned 2 Kitchen\nKnife credits\nFor T&Cs see coles.com.au/smeg-terms.\nShift\nCtrl\nColes Group acknowledges the Traditional\nCustodians of Country throughout Australia.\nWe recognise their strength\nand pay our respects to Elders\npast, present and emerging.\nColes Group extends that respect to all\nAboriginal and Torres Strait Islander peoples.\nand recognises their rich cultures and\ncontinuing connection to land and waters.\nSQUEALING PIG\nFAMILY (750ML)\nfor only\n$13\neach\nOR\nAND\nASAHI SUPER DRY\nBottles (6x330mL)\nfor only\n$13\n6 pk\n*****\nHARD RATED\nCans (10x375mL)\nfor only\nOR $40\nAND\n10 pk\nProant shie dus hit as liquorland. I mil a docut par cumer, par day. This Dacket Deal san ty tab Ndred for usa 6 pack of Bluet\nand Laste of welna uran 10 pack uf R10 Nutable to under bis North WA Bad NT stone card. Doc hot Dialis only vald for\nredempton in Lord KINAL ACT, VIC, WA &\nD25\nBus www liquurtandconsfielp/TermsConditions fur full TADY\nLIQUORLAND\nValid until 07/01/25\n9 31393\n27 82\nflybuys coles\nFUEL DOCKETS ARE NOW\nDIGITAL IN THE FLYBUYS APP\nNever lose a docket again! Plus, we've added a\nnew exclusive in store product offer option.\nAvailable with an activated! Digital Fuel Docket.\nExclusions, limits, T&Cs apply.\nSee flybuys com.au/colasexpress for more details.\ncoles\nColes 4 cents per litre fuel discount available at\ncoles\nexpress\nReddy\nexpress\nReceive 4c/L off the luel pump price or collect B flybuys be\npoints per litre when you present this docket or your digita!\ndocket at your Shell Coles Express or Shell Reddy Express store.\nLimit one receipt por fuel purchase up to 1501, per vehicle,\nper customer. Discount excludes AdBlue, Fleet Card, Shell Card,\nMotoraharge and Motorpass purchases.\nSee in store and www.colesexpress.com.au for Terms and\nConditions.\nShell Coles Express and Shell Reddy Express are owned and\noperated by Viva Energy Retail Pty Lid, which may, in its\ndiscretion, separately make you additional fuel discount offers\nfrom time to time which will be subject to separate Terms and\nConditions.\nExpires: January 17, 2025\n77742291372501170404\nՒՈ";

      //yedi yini yini yini hun vani aso garam vanera garna paryo
      if (!extractReceiptText) {
        return toast.error('Please provide the clearer photo. It is difficult to get data.');
      }

      setFileExtractedRecord(extractReceiptText);

      const formattedData = formatReceiptText(extractReceiptText, vendorRefOptions);
      setRows(formattedData?.expenseRows);

      const globalTaxCalculation = formattedData?.extractTotalIncludingGST
        ? { label: globalTaxCalculationOptions[1]?.value, value: globalTaxCalculationOptions[1]?.value }
        : null; // Ensure it's an object, not an array

      //so in both cases we need to discover vendor, if employee is checked get vendorRef from employee's linkEmployee data ,
      //if company is checked get vendorRef from , formattedData?.extractVendor

      // eslint-disable-next-line no-undef
      const employeeId = localStorage.getItem('userId');

      const payload = {
        // eslint-disable-next-line no-undef
        token: localStorage.getItem('token'),
        employeeId: employeeId
      };

      const result = await linkEmployeeService.getLinkedEmployee(payload);
      const existingLinkEmployee = result.data.data;

      const vendorRef = {
        value: existingLinkEmployee.vendorRef.name,
        label: existingLinkEmployee.vendorRef.name,
        id: existingLinkEmployee.vendorRef.value
      };

      setExpenseFormData((prevFormData) => ({
        ...prevFormData,
        date: formattedData?.extractDate,
        purchaseOrder: formattedData?.extractReferenceNumber,
        vendor: expenseFormData?.paidByCompany ? formattedData?.extractVendor : vendorRef,
        globalTaxCalculation: globalTaxCalculation
      }));
    } catch (error) {
      // eslint-disable-next-line no-console, no-undef
      console.log('error is:', error);
      return {};
      // Optionally, update state to show an error message
    } finally {
      setLoading(false); // Ensures loading state is always updated
    }
  };

  return (
    <div>
      <Grid container spacing={3}>
        {/* paid by  */}
        <Grid item xs={6}>
          <Box component="span" m={1}>
            <Typography>Paid by:</Typography>
          </Box>
        </Grid>

        <Grid item xs={6}>
          <Grid item xs={6} style={{ display: 'flex', alignItems: 'center' }}>
            <Checkbox
              checked={expenseFormData.paidByEmployee}
              onChange={handleExpenseFormData}
              name="paidByEmployee"
              inputProps={{ 'aria-label': 'secondary checkbox' }}
            />
            <Typography>Employee</Typography>
          </Grid>

          <Grid item xs={6} style={{ display: 'flex', alignItems: 'center' }}>
            <Checkbox
              checked={expenseFormData.paidByCompany}
              onChange={handleExpenseFormData}
              name="paidByCompany"
              inputProps={{ 'aria-label': 'secondary checkbox' }}
            />
            <Typography>Company</Typography>
          </Grid>
        </Grid>

        {expenseFormData.paidByCompany && (
          <Grid item container spacing={2} style={{ paddingTop: isSmall && '0px' }}>
            {/* supplier list item */}
            <Grid item xs={12} sm={6}>
              <Typography className={classes.typography}>Supplier*</Typography>
              <ReactSelect
                className="basic-single"
                classNamePrefix="select"
                defaultValue={expenseFormData.vendor}
                isSearchable={true}
                name="vendor"
                // eslint-disable-next-line no-undef
                menuPortalTarget={document.body}
                styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                options={vendorRefOptions}
                onChange={(selectedOption) => {
                  return handleSelectedVendor({ name: selectedOption.value, value: selectedOption.id });
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography className={classes.typography}>Account*</Typography>
              <ReactSelect
                className="basic-single"
                classNamePrefix="select"
                defaultValue={expenseFormData.creditCardAccount}
                isSearchable={true}
                name="creditCardAccount"
                options={creditCardAccountOptions}
                onChange={(selectedOption) => {
                  return handleSelectedCreditCard({ name: selectedOption.value, value: selectedOption.id });
                }}
              />
            </Grid>
          </Grid>
        )}

        <Grid item container spacing={2}>
          {/* date  */}
          <Grid item xs={12} sm={4}>
            <Typography className={classes.typography} style={{ paddingBottom: '2px' }}>
              Date*
            </Typography>
            <TextField
              required={true}
              variant="outlined"
              size="small"
              id="date"
              name="date"
              type="date"
              value={expenseFormData.date}
              onChange={handleExpenseFormData}
              InputLabelProps={{
                shrink: true
              }}
              fullWidth={true}
            />
          </Grid>

          {/* customer item */}
          <Grid item xs={12} sm={4}>
            <Typography className={classes.typography}>Client*</Typography>
            <ReactSelect
              className="basic-single"
              classNamePrefix="select"
              defaultValue={expenseFormData.client}
              isSearchable={true}
              name="client"
              options={customerRefOptions}
              // eslint-disable-next-line no-undef
              menuPortalTarget={document.body}
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
              onChange={(selectedOption) => {
                return handleSelectedClient({ name: selectedOption.value, value: selectedOption.id });
              }}
            />
          </Grid>

          {/* Exc/Inc of Tax */}
          <Grid item xs={12} sm={4}>
            <Typography className={classes.typography}>Exc/Inc of Tax*</Typography>
            <ReactSelect
              className="basic-single"
              classNamePrefix="select"
              value={expenseFormData.globalTaxCalculation} // Use value instead of defaultValue
              isSearchable={true}
              name="globalTaxCalculation"
              options={globalTaxCalculationOptions}
              // eslint-disable-next-line no-undef
              menuPortalTarget={document.body}
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
              onChange={(selectedOption) => {
                return handleSelectedGlobalTaxCalculation({ name: selectedOption.value, value: selectedOption.value });
              }}
            />
          </Grid>

          {isSmall && (
            <Grid item xs={12}>
              <Typography className={classes.typography}>Purchase Order</Typography>
              <TextField
                required={true}
                variant="outlined"
                size="small"
                value={expenseFormData.purchaseOrder}
                onChange={handleExpenseFormData}
                id="purchaseOrder"
                type="text"
                name="purchaseOrder"
                InputLabelProps={{
                  shrink: true
                }}
                fullWidth={true}
              />
            </Grid>
          )}
        </Grid>

        {/* <Grid item container spacing={2}> */}
        {/* Purchase Order */}
        {!isSmall && (
          <Grid item xs={6} sm={4}>
            <Typography className={classes.typography}>Purchase Order</Typography>
            <TextField
              required={true}
              variant="outlined"
              size="small"
              value={expenseFormData.purchaseOrder}
              onChange={handleExpenseFormData}
              id="purchaseOrder"
              type="text"
              name="purchaseOrder"
              fullWidth={true}
            />
          </Grid>
        )}

        {/* variation */}
        <Grid item xs={12} sm={6}>
          <Typography className={classes.typography}>Variation</Typography>
          <Checkbox
            checked={expenseFormData.isBillable}
            onChange={handleExpenseFormData}
            name="isBillable"
            inputProps={{ 'aria-label': 'secondary checkbox' }}
            style={{ paddingTop: '0px' }}
          />
          {expenseFormData.isBillable && (
            <Grid item xs={12}>
              <p>Variation Note*</p>
              <TextField
                id="variationNote"
                type="text"
                name="variationNote"
                variant="outlined"
                multiline
                maxRows={3}
                fullWidth={true}
                value={expenseFormData.variationNote}
                onChange={handleExpenseFormData}
              />
            </Grid>
          )}
        </Grid>
        {/* </Grid> */}
      </Grid>

      <div style={{ margin: '1rem 0', fontSize: '16px' }}>
        *Click
        <b> New </b>
        to fill all the details of expense receipt*
      </div>

      {/* And if we're creating purchase bill, classRef might not be needed  */}
      <AddExpenseDialogTable itemRefs={itemRefs} classRefs={classRefs} rows={rows} setRows={setRows} />

      <Grid item xs={12} style={{ marginTop: '1.5rem', marginBottom: '1.5rem' }}>
        <Typography>Upload an expense receipt.*</Typography>

        <input
          type="file"
          id="expenseReceiptImage"
          name="expenseReceiptImage"
          accept="image/png, image/jpeg"
          onChange={handleExpenseReceiptFileChange}
          style={{ marginTop: '0.6rem' }}
        />
      </Grid>

      {fileExtractedRecord && JSON.stringify(fileExtractedRecord, null, 2)}

      <div>
        {/* <Button onClick={handleReceiptUpload} variant="contained" color="primary" disabled={loading || !receiptFile}> */}
        {/* comment above as it hit api every time */}
        <Button onClick={handleReceiptUpload} variant="contained" color="primary">
          Extract text
        </Button>
      </div>
    </div>
  );
};

export default AddExpenseDialogContent;

AddExpenseDialogContent.propTypes = {
  expenseFormData: PropTypes.any,
  customerRefs: PropTypes.array,
  classRefs: PropTypes.array,
  setExpenseFormData: PropTypes.func,
  itemRefs: PropTypes.array,
  vendorRefs: PropTypes.array,
  creditCardAccount: PropTypes.array,
  rows: PropTypes.array,
  setRows: PropTypes.func
};
